import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import App from '@/App';
import { ApiRequestError } from '@/lib/api';
import { SessionProvider } from '@/lib/session';
import '@/styles/theme.css';

/**
 * One query client for the app.
 *
 * The retry rule is the important part: an `ApiRequestError` is the server
 * answering, not failing. Retrying a 403 three times gets three 403s and a
 * three-second wait before the screen says so. Anything else - a dropped
 * connection, a 500 - is worth one more go.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => !(error instanceof ApiRequestError) && failureCount < 2,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('index.html is missing #root.');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* Inside the router, so the guards it renders can navigate. */}
        <SessionProvider>
          <App />
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
